import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    setCollabMcqModalVisibility,
    setCollabMcqModalIdProject,
    getProjectQuestions,
    setCollabMcqQuestions,
    getProjects,
    recordMcqResponsePerProject,
    setCollabMcqResp
} from "./vcompSlice";
import { showErrorToast, showSuccessToast } from "../../app/utils/helpers";
import Langs from "../../app/lang/langs";

import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";
import Fade from "@mui/material/Fade";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import CloseIcon from "@mui/icons-material/Close";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";

/* ── Slide-up transition for fullscreen dialog ── */
const SlideUp = React.forwardRef(function SlideUp(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

/* ── Premium colour palette (light theme) ── */
const ORANGE = "#FF9015";
const ORANGE_DARK = "#E07800";
const ORANGE_LIGHT = "rgba(255,144,21,0.08)";
const ORANGE_LIGHT_2 = "rgba(255,144,21,0.04)";
const ORANGE_GLOW = "rgba(255,144,21,0.25)";
const ORANGE_GLOW_STRONG = "rgba(255,144,21,0.45)";
const ORANGE_GRADIENT = "linear-gradient(135deg, #FF8701, #FFC100)";
const ORANGE_GRADIENT_VIVID = "linear-gradient(135deg, #FF8701 0%, #FFB100 50%, #FFC100 100%)";
const SURFACE = "rgba(255,255,255,0.72)";
const SURFACE_SOLID = "rgba(255,255,255,0.92)";
const BG_BASE = "#f7f8fa";
const BG_GRADIENT = "linear-gradient(160deg, #f7f8fa 0%, #eef0f4 50%, #e8eaef 100%)";
const TEXT_PRIMARY = "#111318";
const TEXT_SECONDARY = "#4b5263";
const TEXT_MUTED = "#8b92a0";
const BORDER_SUBTLE = "rgba(0,0,0,0.06)";
const BORDER_GLASS = "rgba(0,0,0,0.08)";
const RED = "#dc2626";
const RED_GLOW = "rgba(220,38,38,0.25)";
const GREEN = "#16a34a";
const GREEN_GLOW = "rgba(22,163,74,0.25)";
const EASE = "cubic-bezier(0.4,0,0.2,1)";
const EASE_OUT = "cubic-bezier(0,0,0.2,1)";
const GLASS_BG = "rgba(255,255,255,0.65)";
const GLASS_BG_HOVER = "rgba(255,255,255,0.80)";
const CARD_BG = "rgba(255,255,255,0.72)";
const CARD_BG_ANSWERED = "rgba(255,144,21,0.05)";
const CHIP_BG = "rgba(0,0,0,0.05)";
const CHIP_ACTIVE_BG = ORANGE_GRADIENT;

export default function MobileCollabMcqView() {

    const dispatch = useDispatch();

    /* ── Selectors (identical to desktop) ── */
    const visible = useSelector((state) => state.vcomp.collabMcqModalVisibility);
    const idProject = useSelector((state) => state.vcomp.collabMcqModalIdProject);
    const questions = useSelector((state) => state.vcomp.collabMcqQuestions);
    const projectsList = useSelector((state) => state.vcomp.projectsList);
    const respProjectMcq = useSelector((state) => state.vcomp.collabMcqResp);

    /* ── Local state (identical to desktop) ── */
    const [projectName, setProjectName] = useState('');
    const [selections, setSelections] = useState({});
    const [timeRemaining, setTimeRemaining] = useState(0);
    const [timerActive, setTimerActive] = useState(false);

    /* ── Effects (identical to desktop) ── */
    useEffect(() => {
        if (visible && idProject) {
            const project = projectsList.find(p => p.id == idProject);
            if (project) {
                setProjectName(project.nom);
            }
            dispatch(getProjectQuestions({ id_project: idProject }));
        } else {
            setTimerActive(false);
        }
    }, [visible]);

    useEffect(() => {
        let timer;
        if (questions.length > 0) {
            setTimeRemaining(120 * questions.length);
            timer = setInterval(() => {
                setTimeRemaining(time => {
                    if (time <= 1) {
                        clearInterval(timer);
                        return 0;
                    }
                    return time - 1;
                });
            }, 1000);
        } else {
            if (timer) {
                clearInterval(timer);
            }
        }
        return () => {
            if (timer) {
                clearInterval(timer);
            }
        };
    }, [questions]);

    useEffect(() => {
        if (questions.length > 0 && timeRemaining < 1) {
            handleSubmitMcq();
        }
    }, [timeRemaining]);

    useEffect(() => {
        if (respProjectMcq != null) {
            if (respProjectMcq.trim().toLowerCase().includes("successfully")) {
                handleClose();
                showSuccessToast(respProjectMcq);
            } else {
                handleClose();
            }
            dispatch(setCollabMcqResp(null));
        }
    }, [respProjectMcq]);

    /* ── Handlers (identical to desktop) ── */
    const handleClose = () => {
        dispatch(getProjects());
        dispatch(setCollabMcqModalVisibility(false));
        dispatch(setCollabMcqModalIdProject(null));
        dispatch(setCollabMcqQuestions([]));
        setSelections({});
        setProjectName('');
        setTimeRemaining(0);
    };

    const allQuestionsAnswered = questions.every(question => {
        const selection = selections[question.id];
        return Array.isArray(selection) ? selection.length > 0 : selection !== undefined && selection !== null;
    });

    const handleSubmitMcq = () => {
        const response = Object.keys(selections).map(key => ({
            id_question: parseInt(key),
            answers: Array.isArray(selections[key]) ? selections[key] : [selections[key]]
        }));
        dispatch(recordMcqResponsePerProject({
            id_project: idProject,
            response: response
        }));
    };

    const handleSelectionChange = (questionId, optionId, isMulti) => {
        if (isMulti) {
            setSelections(prev => {
                const currentSelections = prev[questionId] || [];
                if (currentSelections.includes(optionId)) {
                    if (currentSelections.length === 1) {
                        return prev;
                    }
                    return {
                        ...prev,
                        [questionId]: currentSelections.filter(id => id !== optionId)
                    };
                } else {
                    return {
                        ...prev,
                        [questionId]: [...currentSelections, optionId]
                    };
                }
            });
        } else {
            setSelections(prev => ({
                ...prev,
                [questionId]: prev[questionId] === optionId ? undefined : optionId
            }));
        }
    };

    /* ── Timer helpers ── */
    const totalTime = useMemo(() => 120 * questions.length, [questions.length]);
    const timerPercent = totalTime > 0 ? (timeRemaining / totalTime) * 100 : 0;
    const isTimeLow = timeRemaining > 0 && timeRemaining <= 59;
    const timerColor = isTimeLow ? RED : ORANGE;

    function formatTime(seconds) {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }

    /* ── Count answered questions ── */
    const answeredCount = questions.filter(q => {
        const s = selections[q.id];
        return Array.isArray(s) ? s.length > 0 : s !== undefined && s !== null;
    }).length;

    /* ── Refs for scroll-to-question ── */
    const scrollContainerRef = useRef(null);
    const questionRefs = useRef({});
    const chipScrollRef = useRef(null);

    const scrollToQuestion = useCallback((questionId) => {
        const el = questionRefs.current[questionId];
        const container = scrollContainerRef.current;
        if (el && container) {
            const containerTop = container.getBoundingClientRect().top;
            const elTop = el.getBoundingClientRect().top;
            const offset = elTop - containerTop + container.scrollTop - 16;
            container.scrollTo({ top: offset, behavior: "smooth" });
        }
    }, []);

    /* ── Keyframes ── */
    const keyframes = `
        @keyframes mcqMobileFadeInUp {
            from { opacity: 0; transform: translateY(20px) scale(0.98); }
            to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes mcqMobileTimerPulse {
            0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 ${RED_GLOW}; }
            50%      { transform: scale(1.03); box-shadow: 0 0 20px 2px ${RED_GLOW}; }
        }
        @keyframes mcqMobileShimmer {
            0%   { background-position: -200% center; }
            100% { background-position: 200% center; }
        }
        @keyframes mcqMobileGlowPulse {
            0%, 100% { opacity: 0.4; }
            50%      { opacity: 0.8; }
        }
        @keyframes mcqMobileFloat {
            0%, 100% { transform: translateY(0px); }
            50%      { transform: translateY(-6px); }
        }
        @keyframes mcqMobileOrbDrift {
            0%   { transform: translate(0, 0) scale(1); }
            33%  { transform: translate(30px, -20px) scale(1.1); }
            66%  { transform: translate(-20px, 10px) scale(0.95); }
            100% { transform: translate(0, 0) scale(1); }
        }
        @keyframes mcqMobileGradientShift {
            0%   { background-position: 0% 50%; }
            50%  { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
        }
        @keyframes mcqMobileChipBounce {
            0%   { transform: scale(1); }
            40%  { transform: scale(0.88); }
            70%  { transform: scale(1.05); }
            100% { transform: scale(1); }
        }
        @keyframes mcqMobileCheckPop {
            0%   { transform: scale(0) rotate(-15deg); opacity: 0; }
            60%  { transform: scale(1.15) rotate(5deg); opacity: 1; }
            100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        @keyframes mcqMobileBarFill {
            from { transform: scaleX(0); }
            to   { transform: scaleX(1); }
        }
        @keyframes mcqMobileBottomSlideUp {
            from { transform: translateY(100%); opacity: 0; }
            to   { transform: translateY(0); opacity: 1; }
        }
    `;

    return (
        <>
            <style>{keyframes}</style>
            <Dialog
                fullScreen
                open={visible}
                onClose={() => {}}
                slots={{ transition: SlideUp }}
                slotProps={{
                    transition: { timeout: 400 },
                    paper: {
                        sx: {
                            background: BG_BASE,
                            overflow: "hidden",
                            position: "relative",
                        }
                    }
                }}
                sx={{ zIndex: 1400 }}
            >
                {/* ── Animated mesh gradient background ── */}
                <Box sx={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 0,
                    background: BG_GRADIENT,
                    backgroundSize: "200% 200%",
                    animation: "mcqMobileGradientShift 12s ease infinite",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: "-30%",
                        right: "-20%",
                        width: "60vw",
                        height: "60vw",
                        borderRadius: "50%",
                        background: "radial-gradient(circle, rgba(255,144,21,0.10) 0%, transparent 70%)",
                        animation: "mcqMobileOrbDrift 18s ease-in-out infinite",
                    },
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: "-20%",
                        left: "-15%",
                        width: "50vw",
                        height: "50vw",
                        borderRadius: "50%",
                        background: "radial-gradient(circle, rgba(255,144,21,0.06) 0%, transparent 70%)",
                        animation: "mcqMobileOrbDrift 22s ease-in-out infinite reverse",
                    }
                }} />

                {/* ── Glassmorphic AppBar ── */}
                <AppBar
                    position="sticky"
                    elevation={0}
                    sx={{
                        background: "rgba(255,255,255,0.82)",
                        backdropFilter: "blur(24px) saturate(1.5)",
                        WebkitBackdropFilter: "blur(24px) saturate(1.5)",
                        borderBottom: `1px solid ${BORDER_GLASS}`,
                        color: TEXT_PRIMARY,
                        zIndex: 2,
                        /* Orange glow line at bottom */
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            bottom: -1,
                            left: "10%",
                            right: "10%",
                            height: "1px",
                            background: ORANGE_GRADIENT,
                            opacity: 0.6,
                            filter: "blur(0.5px)",
                        }
                    }}
                >
                    <Toolbar sx={{
                        justifyContent: "space-between",
                        px: 1.5,
                        minHeight: 60,
                        gap: 1,
                    }}>
                        {/* Left: project info */}
                        <Stack direction="row" alignItems="center" spacing={1.2} sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{
                                width: 36, height: 36, borderRadius: "11px",
                                background: "rgba(255,144,21,0.10)",
                                border: "1px solid rgba(255,144,21,0.15)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                flexShrink: 0,
                                position: "relative",
                                overflow: "hidden",
                                "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    inset: 0,
                                    borderRadius: "11px",
                                    background: `linear-gradient(135deg, rgba(255,144,21,0.15), transparent)`,
                                }
                            }}>
                                <QuizOutlinedIcon sx={{ color: ORANGE, fontSize: 19, zIndex: 1 }} />
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography noWrap sx={{
                                    fontSize: "1rem", fontWeight: 650,
                                    letterSpacing: "-0.02em", color: TEXT_PRIMARY,
                                    lineHeight: 1.2,
                                    fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                                }}>
                                    {projectName || "Quiz"}
                                </Typography>
                                {questions.length > 0 && (
                                    <Typography sx={{
                                        fontSize: "0.72rem", color: TEXT_MUTED, fontWeight: 500,
                                        fontFamily: "'Inter',sans-serif",
                                        mt: 0.15,
                                    }}>
                                        {answeredCount}/{questions.length} répondue{answeredCount !== 1 ? "s" : ""}
                                    </Typography>
                                )}
                            </Box>
                        </Stack>

                        {/* Right: Timer */}
                        {questions.length > 0 && (
                            <Fade in timeout={600}>
                                <Stack direction="row" alignItems="center" spacing={0.8}
                                    sx={{
                                        px: 1.4, py: 0.6,
                                        borderRadius: "12px",
                                        background: isTimeLow
                                            ? "rgba(239,68,68,0.10)"
                                            : "rgba(255,144,21,0.08)",
                                        border: `1px solid ${isTimeLow
                                            ? "rgba(239,68,68,0.20)"
                                            : "rgba(255,144,21,0.15)"}`,
                                        transition: `all 400ms ${EASE}`,
                                        animation: isTimeLow ? "mcqMobileTimerPulse 1.2s ease-in-out infinite" : "none",
                                        flexShrink: 0,
                                        boxShadow: isTimeLow
                                            ? `0 0 20px ${RED_GLOW}, inset 0 0 12px rgba(239,68,68,0.06)`
                                            : `0 0 16px rgba(255,144,21,0.08)`,
                                    }}
                                >
                                    <Box sx={{ position: "relative", display: "inline-flex" }}>
                                        <CircularProgress
                                            variant="determinate"
                                            value={timerPercent}
                                            size={28}
                                            thickness={3}
                                            sx={{
                                                color: timerColor,
                                                transition: `color 400ms ${EASE}`,
                                                filter: isTimeLow ? `drop-shadow(0 0 4px ${RED_GLOW})` : `drop-shadow(0 0 3px ${ORANGE_GLOW})`,
                                                "& .MuiCircularProgress-circle": {
                                                    transition: "stroke-dashoffset 1s linear",
                                                    strokeLinecap: "round",
                                                }
                                            }}
                                        />
                                        <Box sx={{
                                            position: "absolute", inset: 0,
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                        }}>
                                            <TimerOutlinedIcon sx={{
                                                fontSize: 12, color: timerColor,
                                                transition: `color 400ms ${EASE}`,
                                            }} />
                                        </Box>
                                    </Box>
                                    <Typography sx={{
                                        fontFamily: "'JetBrains Mono','SF Mono',monospace",
                                        fontSize: "0.9rem",
                                        fontWeight: 700,
                                        letterSpacing: "0.05em",
                                        color: timerColor,
                                        transition: `color 400ms ${EASE}`,
                                        minWidth: "50px",
                                        textShadow: isTimeLow
                                            ? `0 0 12px ${RED_GLOW}`
                                            : `0 0 8px ${ORANGE_GLOW}`,
                                    }}>
                                        {formatTime(timeRemaining)}
                                    </Typography>
                                </Stack>
                            </Fade>
                        )}
                    </Toolbar>
                </AppBar>

                {/* ── Body ── */}
                {questions.length > 0 ? (
                    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", position: "relative", zIndex: 1 }}>

                        {/* ── Sticky horizontally scrollable progress chips bar ── */}
                        <Box sx={{
                            position: "sticky",
                            top: 0,
                            zIndex: 5,
                            background: "rgba(255,255,255,0.85)",
                            backdropFilter: "blur(20px) saturate(1.3)",
                            WebkitBackdropFilter: "blur(20px) saturate(1.3)",
                            borderBottom: `1px solid ${BORDER_GLASS}`,
                            py: 1.2,
                            px: 0,
                        }}>
                            {/* Subtle connecting line behind chips */}
                            <Box sx={{
                                position: "absolute",
                                top: "50%",
                                left: 24,
                                right: 24,
                                height: "1px",
                                background: `linear-gradient(90deg, transparent, rgba(0,0,0,0.06), transparent)`,
                                transform: "translateY(-50%)",
                                zIndex: 0,
                            }} />
                            <Box
                                ref={chipScrollRef}
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: 0.7,
                                    overflowX: "auto",
                                    px: 1.5,
                                    pb: 0.5,
                                    WebkitOverflowScrolling: "touch",
                                    scrollbarWidth: "none",
                                    "&::-webkit-scrollbar": { display: "none" },
                                    position: "relative",
                                    zIndex: 1,
                                }}
                            >
                                {questions.map((q, i) => {
                                    const s = selections[q.id];
                                    const answered = Array.isArray(s) ? s.length > 0 : s !== undefined && s !== null;
                                    return (
                                        <Box
                                            key={q.id}
                                            onClick={() => scrollToQuestion(q.id)}
                                            sx={{
                                                width: 38, height: 38,
                                                minWidth: 38,
                                                borderRadius: "11px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "0.78rem",
                                                fontWeight: 700,
                                                fontFamily: "'Inter',sans-serif",
                                                cursor: "pointer",
                                                userSelect: "none",
                                                WebkitTapHighlightColor: "transparent",
                                                background: answered
                                                    ? ORANGE_GRADIENT
                                                    : CHIP_BG,
                                                color: answered ? "#fff" : TEXT_MUTED,
                                                border: `1px solid ${answered ? "transparent" : "rgba(0,0,0,0.08)"}`,
                                                transition: `all 300ms ${EASE}`,
                                                boxShadow: answered
                                                    ? `0 2px 12px ${ORANGE_GLOW}, 0 0 20px rgba(255,144,21,0.10)`
                                                    : "none",
                                                position: "relative",
                                                overflow: "hidden",
                                                /* Shimmer effect on answered chips */
                                                ...(answered && {
                                                    "&::after": {
                                                        content: '""',
                                                        position: "absolute",
                                                        inset: 0,
                                                        background: "linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.20) 50%, transparent 70%)",
                                                        backgroundSize: "200% 100%",
                                                        animation: "mcqMobileShimmer 3s ease-in-out infinite",
                                                    }
                                                }),
                                                "&:active": {
                                                    animation: "mcqMobileChipBounce 300ms ease",
                                                },
                                            }}
                                        >
                                            {i + 1}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>

                        {/* ── Scrollable questions area ── */}
                        <Box
                            ref={scrollContainerRef}
                            sx={{
                                flex: 1,
                                overflowY: "auto",
                                overflowX: "hidden",
                                pb: "140px",
                                pt: 2,
                                px: 1.5,
                                WebkitOverflowScrolling: "touch",
                                scrollbarWidth: "none",
                                "&::-webkit-scrollbar": { display: "none" },
                            }}
                        >
                            {/* Question cards */}
                            {questions.map((question, idx) => {
                                const isAnswered = (() => {
                                    const s = selections[question.id];
                                    return Array.isArray(s) ? s.length > 0 : s !== undefined && s !== null;
                                })();

                                return (
                                    <Paper
                                        key={question.id}
                                        ref={(el) => { questionRefs.current[question.id] = el; }}
                                        elevation={0}
                                        sx={{
                                            mb: 2,
                                            borderRadius: "18px",
                                            background: isAnswered ? CARD_BG_ANSWERED : CARD_BG,
                                            backdropFilter: "blur(16px) saturate(1.2)",
                                            WebkitBackdropFilter: "blur(16px) saturate(1.2)",
                                            border: `1px solid ${isAnswered ? "rgba(255,144,21,0.15)" : BORDER_GLASS}`,
                                            overflow: "hidden",
                                            position: "relative",
                                            transition: `all 350ms ${EASE}`,
                                            animation: `mcqMobileFadeInUp 500ms ${EASE_OUT} ${idx * 80}ms both`,
                                            boxShadow: isAnswered
                                                ? `0 4px 24px rgba(255,144,21,0.06), 0 1px 8px rgba(0,0,0,0.2)`
                                                : `0 2px 12px rgba(0,0,0,0.15), 0 1px 4px rgba(0,0,0,0.1)`,
                                            /* Animated top accent bar */
                                            "&::before": {
                                                content: '""',
                                                position: "absolute",
                                                top: 0, left: 0, right: 0,
                                                height: "2.5px",
                                                background: isAnswered
                                                    ? ORANGE_GRADIENT
                                                    : `linear-gradient(90deg, transparent 10%, rgba(255,144,21,0.15) 50%, transparent 90%)`,
                                                transformOrigin: "left center",
                                                animation: isAnswered ? `mcqMobileBarFill 500ms ${EASE_OUT} both` : "none",
                                                transition: `all 400ms ${EASE}`,
                                                boxShadow: isAnswered ? `0 0 10px ${ORANGE_GLOW}` : "none",
                                            }
                                        }}
                                    >
                                        {/* Question header */}
                                        <Box sx={{
                                            px: 2,
                                            pt: 2,
                                            pb: 1,
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: 1.2,
                                        }}>
                                            <Chip
                                                label={idx + 1}
                                                size="small"
                                                sx={{
                                                    minWidth: 30, height: 28,
                                                    borderRadius: "9px",
                                                    fontWeight: 700,
                                                    fontSize: "0.75rem",
                                                    mt: 0.2,
                                                    background: isAnswered
                                                        ? "rgba(255,144,21,0.15)"
                                                        : "rgba(0,0,0,0.06)",
                                                    color: isAnswered ? ORANGE : TEXT_MUTED,
                                                    border: `1px solid ${isAnswered ? "rgba(255,144,21,0.25)" : "rgba(0,0,0,0.08)"}`,
                                                    transition: `all 350ms ${EASE}`,
                                                    "& .MuiChip-label": { px: 0.8 },
                                                    ...(isAnswered && {
                                                        boxShadow: `0 0 10px rgba(255,144,21,0.12)`,
                                                    }),
                                                }}
                                            />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{
                                                    fontSize: "0.95rem",
                                                    fontWeight: 600,
                                                    letterSpacing: "-0.02em",
                                                    color: TEXT_PRIMARY,
                                                    lineHeight: 1.45,
                                                    fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                                                    wordBreak: "break-word",
                                                }}>
                                                    {question.question}
                                                </Typography>
                                                <Typography sx={{
                                                    fontSize: "0.7rem",
                                                    color: TEXT_MUTED,
                                                    fontWeight: 500,
                                                    mt: 0.3,
                                                    fontFamily: "'Inter',sans-serif",
                                                    letterSpacing: "0.02em",
                                                    textTransform: "uppercase",
                                                }}>
                                                    {question.type === "single" ? "Choix unique" : "Choix multiple"}
                                                </Typography>
                                            </Box>
                                            {isAnswered && (
                                                <Box sx={{
                                                    animation: `mcqMobileCheckPop 400ms ${EASE_OUT} both`,
                                                    flexShrink: 0,
                                                    mt: 0.3,
                                                }}>
                                                    <CheckCircleOutlineIcon sx={{
                                                        color: GREEN,
                                                        fontSize: 22,
                                                        filter: `drop-shadow(0 0 6px ${GREEN_GLOW})`,
                                                    }} />
                                                </Box>
                                            )}
                                        </Box>

                                        {/* Divider */}
                                        <Box sx={{
                                            height: "1px", mx: 2,
                                            background: `linear-gradient(90deg, transparent, ${BORDER_GLASS}, transparent)`,
                                        }} />

                                        {/* Options */}
                                        <Box sx={{ px: 1, py: 1 }}>
                                            {question.type === "single" ? (
                                                <RadioGroup
                                                    value={selections[question.id] !== undefined ? selections[question.id] : ""}
                                                    onChange={(e) => {
                                                        const val = parseInt(e.target.value);
                                                        handleSelectionChange(question.id, val, false);
                                                    }}
                                                >
                                                    {question.options.map(option => {
                                                        const isSelected = selections[question.id] === option.id;
                                                        return (
                                                            <FormControlLabel
                                                                key={option.id}
                                                                value={option.id}
                                                                control={
                                                                    <Radio
                                                                        size="small"
                                                                        sx={{
                                                                            color: "rgba(0,0,0,0.25)",
                                                                            "&.Mui-checked": {
                                                                                color: ORANGE,
                                                                                filter: `drop-shadow(0 0 4px ${ORANGE_GLOW})`,
                                                                            },
                                                                            transition: `all 250ms ${EASE}`,
                                                                            p: 1,
                                                                        }}
                                                                    />
                                                                }
                                                                label={
                                                                    <Typography sx={{
                                                                        fontSize: "0.9rem",
                                                                        fontWeight: isSelected ? 550 : 420,
                                                                        color: isSelected ? TEXT_PRIMARY : TEXT_SECONDARY,
                                                                        transition: `all 250ms ${EASE}`,
                                                                        fontFamily: "'Inter',sans-serif",
                                                                        lineHeight: 1.4,
                                                                    }}>
                                                                        {option.value}
                                                                    </Typography>
                                                                }
                                                                sx={{
                                                                    mx: 0,
                                                                    mb: 0.4,
                                                                    px: 1.2,
                                                                    py: 0.7,
                                                                    borderRadius: "13px",
                                                                    minHeight: 48,
                                                                    border: `1px solid ${isSelected ? "rgba(255,144,21,0.22)" : "transparent"}`,
                                                                    background: isSelected
                                                                        ? "rgba(255,144,21,0.08)"
                                                                        : "transparent",
                                                                    transition: `all 250ms ${EASE}`,
                                                                    WebkitTapHighlightColor: "transparent",
                                                                    ...(isSelected && {
                                                                        boxShadow: `inset 0 0 20px rgba(255,144,21,0.04), 0 0 12px rgba(255,144,21,0.05)`,
                                                                    }),
                                                                    "&:active": {
                                                                        background: isSelected
                                                                            ? "rgba(255,144,21,0.12)"
                                                                            : "rgba(0,0,0,0.03)",
                                                                        transform: "scale(0.985)",
                                                                    },
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </RadioGroup>
                                            ) : (
                                                <FormGroup>
                                                    {question.options.map(option => {
                                                        const isSelected = selections[question.id]
                                                            ? selections[question.id].includes(option.id)
                                                            : false;
                                                        return (
                                                            <FormControlLabel
                                                                key={option.id}
                                                                control={
                                                                    <Checkbox
                                                                        size="small"
                                                                        checked={isSelected}
                                                                        onChange={() => handleSelectionChange(question.id, option.id, true)}
                                                                        sx={{
                                                                            color: "rgba(0,0,0,0.25)",
                                                                            "&.Mui-checked": {
                                                                                color: ORANGE,
                                                                                filter: `drop-shadow(0 0 4px ${ORANGE_GLOW})`,
                                                                            },
                                                                            transition: `all 250ms ${EASE}`,
                                                                            borderRadius: "7px",
                                                                            p: 1,
                                                                        }}
                                                                    />
                                                                }
                                                                label={
                                                                    <Typography sx={{
                                                                        fontSize: "0.9rem",
                                                                        fontWeight: isSelected ? 550 : 420,
                                                                        color: isSelected ? TEXT_PRIMARY : TEXT_SECONDARY,
                                                                        transition: `all 250ms ${EASE}`,
                                                                        fontFamily: "'Inter',sans-serif",
                                                                        lineHeight: 1.4,
                                                                    }}>
                                                                        {option.value}
                                                                    </Typography>
                                                                }
                                                                sx={{
                                                                    mx: 0,
                                                                    mb: 0.4,
                                                                    px: 1.2,
                                                                    py: 0.7,
                                                                    borderRadius: "13px",
                                                                    minHeight: 48,
                                                                    border: `1px solid ${isSelected ? "rgba(255,144,21,0.22)" : "transparent"}`,
                                                                    background: isSelected
                                                                        ? "rgba(255,144,21,0.08)"
                                                                        : "transparent",
                                                                    transition: `all 250ms ${EASE}`,
                                                                    WebkitTapHighlightColor: "transparent",
                                                                    ...(isSelected && {
                                                                        boxShadow: `inset 0 0 20px rgba(255,144,21,0.04), 0 0 12px rgba(255,144,21,0.05)`,
                                                                    }),
                                                                    "&:active": {
                                                                        background: isSelected
                                                                            ? "rgba(255,144,21,0.12)"
                                                                            : "rgba(0,0,0,0.03)",
                                                                        transform: "scale(0.985)",
                                                                    },
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </FormGroup>
                                            )}
                                        </Box>
                                    </Paper>
                                );
                            })}
                        </Box>
                    </Box>
                ) : (
                    /* ── Empty state ── */
                    <Box sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        px: 3,
                        animation: `mcqMobileFadeInUp 500ms ${EASE_OUT} both`,
                        position: "relative",
                        zIndex: 1,
                    }}>
                        <Box sx={{
                            width: 72, height: 72, borderRadius: "20px",
                            background: "rgba(255,144,21,0.08)",
                            border: "1px solid rgba(255,144,21,0.15)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            mb: 1,
                            position: "relative",
                            animation: "mcqMobileFloat 4s ease-in-out infinite",
                            boxShadow: `0 8px 32px rgba(255,144,21,0.10)`,
                            "&::after": {
                                content: '""',
                                position: "absolute",
                                inset: -4,
                                borderRadius: "24px",
                                border: "1px solid rgba(255,144,21,0.08)",
                                animation: "mcqMobileGlowPulse 3s ease-in-out infinite",
                            }
                        }}>
                            <QuizOutlinedIcon sx={{
                                fontSize: 32, color: ORANGE,
                                filter: `drop-shadow(0 0 8px ${ORANGE_GLOW})`,
                            }} />
                        </Box>
                        <Typography sx={{
                            fontSize: "1.15rem", fontWeight: 650,
                            color: TEXT_PRIMARY, letterSpacing: "-0.02em",
                            fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                            textAlign: "center",
                        }}>
                            Aucune question
                        </Typography>
                        <Typography sx={{
                            fontSize: "0.85rem", color: TEXT_MUTED, fontWeight: 400,
                            fontFamily: "'Inter',sans-serif",
                            textAlign: "center",
                            lineHeight: 1.6,
                            maxWidth: 260,
                        }}>
                            Il n'y a pas de questions pour ce projet.
                        </Typography>
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => handleClose()}
                            startIcon={<CloseIcon sx={{ fontSize: 18 }} />}
                            sx={{
                                mt: 2,
                                borderRadius: "14px",
                                px: 3, py: 1.2,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.9rem",
                                minHeight: 48,
                                color: RED,
                                borderColor: "rgba(239,68,68,0.25)",
                                fontFamily: "'Inter',sans-serif",
                                transition: `all 250ms ${EASE}`,
                                maxWidth: 280,
                                background: "rgba(239,68,68,0.05)",
                                backdropFilter: "blur(8px)",
                                "&:hover": {
                                    borderColor: "rgba(239,68,68,0.35)",
                                    background: "rgba(239,68,68,0.08)",
                                },
                                "&:active": {
                                    transform: "scale(0.97)",
                                    boxShadow: `0 0 16px ${RED_GLOW}`,
                                },
                            }}
                        >
                            <Langs str="CLOSE" />
                        </Button>
                    </Box>
                )}

                {/* ── Sticky bottom bar ── */}
                {questions.length > 0 && (
                    <Box sx={{
                        position: "fixed",
                        bottom: 0, left: 0, right: 0,
                        zIndex: 10,
                        background: "rgba(255,255,255,0.88)",
                        backdropFilter: "blur(24px) saturate(1.5)",
                        WebkitBackdropFilter: "blur(24px) saturate(1.5)",
                        borderTop: `1px solid ${BORDER_GLASS}`,
                        px: 1.5,
                        pt: 1.5,
                        pb: "max(14px, env(safe-area-inset-bottom))",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "stretch",
                        gap: 1,
                        animation: `mcqMobileBottomSlideUp 500ms ${EASE_OUT} 300ms both`,
                        /* Top glow line */
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            top: -1,
                            left: "15%",
                            right: "15%",
                            height: "1px",
                            background: ORANGE_GRADIENT,
                            opacity: 0.35,
                        }
                    }}>
                        {/* Progress + Submit row */}
                        <Stack direction="row" alignItems="center" spacing={1.2}>
                            {/* Progress indicator */}
                            <Chip
                                label={`${answeredCount} / ${questions.length}`}
                                size="small"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: "0.78rem",
                                    fontFamily: "'Inter',sans-serif",
                                    background: allQuestionsAnswered
                                        ? "rgba(34,197,94,0.12)"
                                        : "rgba(0,0,0,0.06)",
                                    color: allQuestionsAnswered ? GREEN : TEXT_MUTED,
                                    border: `1px solid ${allQuestionsAnswered
                                        ? "rgba(34,197,94,0.25)"
                                        : "rgba(0,0,0,0.08)"}`,
                                    borderRadius: "10px",
                                    transition: `all 350ms ${EASE}`,
                                    "& .MuiChip-label": { px: 1.2 },
                                    flexShrink: 0,
                                    ...(allQuestionsAnswered && {
                                        boxShadow: `0 0 12px ${GREEN_GLOW}`,
                                    }),
                                }}
                            />

                            {/* Submit button — full width on mobile */}
                            <Button
                                variant="contained"
                                fullWidth
                                disabled={!allQuestionsAnswered}
                                onClick={() => handleSubmitMcq()}
                                endIcon={<SendRoundedIcon sx={{ fontSize: "18px !important" }} />}
                                sx={{
                                    borderRadius: "14px",
                                    py: 1.4,
                                    textTransform: "none",
                                    fontWeight: 650,
                                    fontSize: "0.95rem",
                                    letterSpacing: "-0.01em",
                                    fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                                    minHeight: 50,
                                    background: ORANGE_GRADIENT_VIVID,
                                    color: "#fff",
                                    border: "none",
                                    position: "relative",
                                    overflow: "hidden",
                                    boxShadow: `0 4px 20px ${ORANGE_GLOW}, 0 2px 8px rgba(255,144,21,0.20)`,
                                    transition: `all 300ms ${EASE}`,
                                    /* Shimmer overlay */
                                    "&::after": {
                                        content: '""',
                                        position: "absolute",
                                        inset: 0,
                                        background: "linear-gradient(110deg, transparent 25%, rgba(255,255,255,0.18) 50%, transparent 75%)",
                                        backgroundSize: "200% 100%",
                                        animation: "mcqMobileShimmer 4s ease-in-out infinite",
                                    },
                                    "&:active": {
                                        transform: "scale(0.97)",
                                        boxShadow: `0 2px 12px ${ORANGE_GLOW}`,
                                    },
                                    "&.Mui-disabled": {
                                        background: "rgba(0,0,0,0.06)",
                                        color: "rgba(0,0,0,0.26)",
                                        boxShadow: "none",
                                        border: `1px solid rgba(0,0,0,0.06)`,
                                        "&::after": { display: "none" },
                                    },
                                }}
                            >
                                Soumettre
                            </Button>
                        </Stack>
                    </Box>
                )}
            </Dialog>
        </>
    );
}
